import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Menu, ChevronUp, ChevronDown, Clock } from 'lucide-react';

const RECENT_TABS_KEY = 'campaign-tracker-recent-tabs';
const MAX_RECENT = 5;

const TAB_CATEGORIES: { label: string; tabs: string[] }[] = [
  { label: 'Characters & Factions', tabs: ['characters', 'npcs', 'organizations', 'heat'] },
  { label: 'World', tabs: ['locations', 'maps', 'calendar', 'timelines', 'wiki'] },
  { label: 'Story', tabs: ['plots', 'sessions', 'future', 'encounters'] },
  { label: 'Tools', tabs: ['oracle', 'generators', 'whiteboards', 'content-trees', 'sheets', 'rules', 'play'] },
  { label: 'Meta', tabs: ['overview', 'assistant', 'publish', 'memory', 'integrations', 'data'] },
];

function getRecentTabs(): string[] {
  try {
    const stored = localStorage.getItem(RECENT_TABS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function addRecentTab(tab: string) {
  const recent = getRecentTabs().filter((t) => t !== tab);
  recent.unshift(tab);
  localStorage.setItem(RECENT_TABS_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
}

interface MobileWorkspaceSwitcherProps {
  tabs: string[];
  activeTab: string;
  getTabLabel: (tab: string) => string;
  onTabChange: (tab: string) => void;
}

export default function MobileWorkspaceSwitcher({
  tabs,
  activeTab,
  getTabLabel,
  onTabChange,
}: MobileWorkspaceSwitcherProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});
  const [recentTabs, setRecentTabs] = useState<string[]>(getRecentTabs);
  const filterRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isExpanded && filterRef.current) {
      filterRef.current.focus();
    }
  }, [isExpanded]);

  const normalizedQuery = filterQuery.trim().toLowerCase();

  const filteredCategories = useMemo(() => {
    return TAB_CATEGORIES.map((cat) => ({
      ...cat,
      tabs: cat.tabs.filter(
        (tab) => tabs.includes(tab) && (!normalizedQuery || getTabLabel(tab).toLowerCase().includes(normalizedQuery))
      ),
    })).filter((cat) => cat.tabs.length > 0);
  }, [normalizedQuery, getTabLabel, tabs]);

  const recentFiltered = useMemo(() => {
    return recentTabs
      .filter((tab) => tabs.includes(tab) && (!normalizedQuery || getTabLabel(tab).toLowerCase().includes(normalizedQuery)))
      .slice(0, MAX_RECENT);
  }, [recentTabs, tabs, normalizedQuery, getTabLabel]);

  const handleTabClick = useCallback((tab: string) => {
    onTabChange(tab);
    addRecentTab(tab);
    setRecentTabs(getRecentTabs());
    setIsExpanded(false);
    setFilterQuery('');
  }, [onTabChange]);

  const toggleCategory = useCallback((label: string) => {
    setCollapsedCategories((prev) => ({ ...prev, [label]: !prev[label] }));
  }, []);

  return (
    <div className="mobile-workspace-switcher">
      <div className="mobile-workspace-shell">
        <div className="mobile-workspace-top">
          <button
            type="button"
            data-testid="mobile-workspace-toggle"
            onClick={() => setIsExpanded((prev) => !prev)}
            className="mobile-workspace-toggle"
          >
            <Menu size={14} />
            <span>Modules</span>
            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          <div className="mobile-workspace-active">
            <span className="mobile-workspace-active-label">Current</span>
            <span className="mobile-workspace-active-value">{getTabLabel(activeTab)}</span>
          </div>
        </div>

        {isExpanded && (
          <div className="mobile-workspace-panel">
            <input
              ref={filterRef}
              value={filterQuery}
              onChange={(event) => setFilterQuery(event.target.value)}
              placeholder="Filter modules..."
              data-testid="mobile-workspace-filter"
              className="mobile-workspace-filter"
            />

            {/* Recent tabs */}
            {recentFiltered.length > 0 && !normalizedQuery && (
              <div style={{ marginTop: '0.5rem', marginBottom: '0.25rem' }}>
                <div className="field-label" style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.35rem' }}>
                  <Clock size={11} /> Recent
                </div>
                <div className="mobile-workspace-grid">
                  {recentFiltered.map((tab) => (
                    <button
                      key={`recent-${tab}`}
                      type="button"
                      data-testid={`mobile-workspace-recent-${tab}`}
                      onClick={() => handleTabClick(tab)}
                      className={`mobile-workspace-tab ${activeTab === tab ? 'mobile-workspace-tab-active' : ''}`}
                    >
                      {getTabLabel(tab)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Categorized tabs */}
            {filteredCategories.map((cat) => (
              <div key={cat.label} style={{ marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => toggleCategory(cat.label)}
                  className="field-label"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    marginBottom: '0.35rem',
                    cursor: 'pointer',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    width: '100%',
                  }}
                >
                  {collapsedCategories[cat.label] ? <ChevronDown size={11} /> : <ChevronUp size={11} />}
                  {cat.label}
                  <span style={{ marginLeft: 'auto', fontSize: '0.6rem', opacity: 0.6 }}>
                    {cat.tabs.length}
                  </span>
                </button>
                {!collapsedCategories[cat.label] && (
                  <div className="mobile-workspace-grid">
                    {cat.tabs.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        data-testid={`mobile-workspace-tab-${tab}`}
                        onClick={() => handleTabClick(tab)}
                        className={`mobile-workspace-tab ${activeTab === tab ? 'mobile-workspace-tab-active' : ''}`}
                      >
                        {getTabLabel(tab)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {filteredCategories.length === 0 && recentFiltered.length === 0 && (
              <div className="mobile-workspace-empty">No modules match your filter.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
